import React, { useEffect, useRef, useState } from "react";
import Cabinet from "../models/Cabinet";
import Actor from "../models/Actor";
import Consumer from "../models/Consumer";
import BaseModel from "../models/BaseModel";
import WatchableValue from "../models/WatchableValue";
import UniversalObjectTable, { ColumnConfig } from "./universalObjectTable/UniversalObjectTable";
import { itx } from "../translationItx/itx";
import { ConnectionType } from "../models/Connection";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import PanelResizer from "./PanelResizer";
import { channelTypeInfo } from "../models/ChannelType";
import { DragMode } from "../models/TreeviewModel";
import Device from "../models/Device";
import Channel from "../models/Channel";
import ObjectReference from "./ObjectReference";
import ConnectableObject from "../models/ConnectableObject";

export type LeftConsumerListProps = {
  device?: Device;
};

function LeftSideList(props: LeftConsumerListProps) {
  const { device } = props;
  const items = useRef(new WatchableValue<BaseModel[]>([]));
  const prevDeviceRef = useRef<Device | undefined>(undefined);
  const selectedObjects = useRef(new WatchableValue<Set<BaseModel>>(new Set()));

  const isSupply = device ? channelTypeInfo(device.channelType).isSupply : false;

  function computeItems(): BaseModel[] {
    if (!device) return [];
    if (channelTypeInfo(device.channelType).isSupply) {
      const consumers: Consumer[] = [];
      device.channels.forEach((ch) => ch.getAllConnectedConsumers().forEach((c) => consumers.push(c)));
      device.knxChannels.forEach((ch) => ch.getAllConnectedConsumers().forEach((c) => consumers.push(c)));
      return consumers;
    }
    return device.channelsSortedForDisplay({
      buttonChannels: device.isButton ? true : false,
      actorChannels: device.isActor ? true : false, // if device is actor, then we will hide actor channels, because they are shown in the right list as connectable consumers, otherwise we will show them, because they are not shown in the right list
      statusChannels: false,
      logicChannels: false,
      environmentChannel: false,
      temperatureMeasurementChannel: false,
      channelWithoutKnxChannel: false,
      generalChannel: false,
      specialChannel: false,
      patchChannels: false,
      longPressChannels: false,
      manualChannel: false,
      unknownChannel: false,
      ignoreChannel: false,
    });
  }

  // Synchronously update items during render when device changes
  if (prevDeviceRef.current !== device) {
    prevDeviceRef.current = device;
    items.current.value = computeItems();
    selectedObjects.current.value = new Set();
    selectedObjects.current.raiseOnChange();
  }

  // Watch channels directly (like KnxButtonTable) for ongoing updates
  useOnChangeWatcher(device?.channels, () => {
    items.current.value = computeItems();
    items.current.raiseOnChange();
    return false;
  });

  // Watch device for connection changes (childCollection raised after assign/disconnect)
  useOnChangeWatcher(device, (changedProperty) => {
    if (changedProperty === "childCollection" || changedProperty === "connections") {
      items.current.value = computeItems();
      items.current.raiseOnChange();
    }
    return false;
  });

  function disconnectSelectedItems() {
    if (!device) return;
    const selected = Array.from(selectedObjects.current.value);
    if (isSupply) {
      selected.forEach((obj) => {
        const consumer = obj as Consumer;
        const channel = consumer.getConnectedChannel();
        if (channel) {
          channel.unlinkConnectionWith(consumer);
        }
      });
    } else {
      selected.forEach((obj) => {
        const channel = obj as Channel;
        channel.getAllConnectedConsumers().forEach((consumer) => channel.unlinkConnectionWith(consumer));
      });
    }
    selectedObjects.current.value = new Set();
    selectedObjects.current.raiseOnChange();
    items.current.value = computeItems();
    items.current.raiseOnChange();
    device.raiseOnChange("childCollection");
  }

  function DisconnectButton() {
    useOnChangeWatcher(selectedObjects.current);
    const count = selectedObjects.current.value.size;
    return (
      <button className="button white" onClick={disconnectSelectedItems} disabled={count === 0}>
        {count} {itx("cabinetData.disconnectSelected", "Verbindungen lösen")}
      </button>
    );
  }

  if (isSupply) {
    return (
      <UniversalObjectTable
        title={itx("cabinetData.leftSideList", "Verbundene Verbraucher")}
        objects={items.current}
        selectedObjects={selectedObjects.current}
        width="100%"
        objectColumnConfig={
          new ColumnConfig({ title: itx("cabinetData.consumer", "Verbraucher"), showName: "afterButtons" }) // no itx
        }
        parentColumnConfig={new ColumnConfig({ title: itx("cabinetData.room", "Raum") })}
        footer={[<DisconnectButton key="disconnect" />]}
      />
    );
  }

  return (
    <UniversalObjectTable
      title={itx("cabinetData.leftSideList", "Kanäle")}
      objects={items.current}
      selectedObjects={selectedObjects.current}
      width="100%"
      objectColumnConfig={
        new ColumnConfig({
          title: itx("cabinetData.channel", "Kanal"),
          showTag: false,
          showName: "never" as any, // no itx
          filterFunc: (obj, expr) => {
            const lower = expr.toLocaleLowerCase();
            const ch = obj as Channel;
            const searchTag = ch.tag || ch.parent?.tag || "";
            const searchName = ch.name || ch.parent?.name || "";
            return searchTag.toLocaleLowerCase().includes(lower) || searchName.toLocaleLowerCase().includes(lower);
          },
        })
      }
      additionalColumns={[
        {
          columnConfig: new ColumnConfig({ title: itx("cabinetData.connectedConsumers", "Verbundene Verbraucher") }),
          position: "right",
          filterFunc: (obj, expr) => {
            const lower = expr.toLocaleLowerCase();
            return (obj as Channel)
              .getAllConnectedConsumers()
              .some((c) => c.tag.toLocaleLowerCase().includes(lower) || c.name.toLocaleLowerCase().includes(lower));
          },
          element: (obj) => {
            const consumers = (obj as Channel).getAllConnectedConsumers();
            if (consumers.length === 0)
              return <span style={{ paddingLeft: 8, color: "var(--text-disabled, #aaa)" }}>–</span>;
            return (
              <div style={{ display: "flex", flexDirection: "row", paddingLeft: 8, gap: 2 }}>
                {consumers.map((c) => (
                  <ObjectReference key={c.id} obj={c} showTag showName="afterButtons" showIcon />
                ))}
              </div>
            );
          },
        },
      ]}
      footer={[<DisconnectButton key="disconnect" />]}
    />
  );
}

function RightSideList(props: { device?: Device }) {
  const { device } = props;

  const selectedObjects = useRef(new WatchableValue<Set<BaseModel>>(new Set()));
  const consumers = useRef(new WatchableValue<BaseModel[]>([]));
  useEffect(() => {
    loadConsumerList();
  }, [device]);
  useOnChangeWatcher(device, (changedProperty) => {
    if (changedProperty === "channels" || changedProperty === "childCollection") {
      loadConsumerList();
    }
    return false;
  });

  function loadConsumerList() {
    const connectableConsumers = new Array<ConnectableObject>();
    state.allConsumers().forEach((consumer) => {
      if (
        device?.channels.asArray().some((channel) => channel.isConnectable(ConnectionType.Channel, consumer)) ||
        (device?.isActor && consumer.channelType == device.channelType)
      ) {
        // check first if consumer is allready connected with one channel of this actor, if yes, then it should not be shown in the list of connectable consumers
        const channelFromConsumer = consumer.getConnectedChannel();
        if (channelFromConsumer?.parent?.id === device.id) {
          return;
        }
        connectableConsumers.push(consumer);
      }
    });
    // for button we will add scenes and multiplexer to the list of connectable consumers, because they can also be connected to buttons, even if they are not connectable to channels
    if (device && device.isButton) {
      state.allScenes().forEach((scene) => {
        connectableConsumers.push(scene);
      });
    }
    consumers.current.value = connectableConsumers;
    consumers.current.raiseOnChange();
  }

  function assignSelectedConsumers() {
    if (!device) return;
    const selected = Array.from(selectedObjects.current.value) as ConnectableObject[];
    selected.forEach((connectableObj) => {
      device.treeViewModel.handleDrop2(DragMode.link, connectableObj);
    });
    selectedObjects.current.value = new Set();
    selectedObjects.current.raiseOnChange();
    device.raiseOnChange("childCollection");
    loadConsumerList();
  }

  function AssignButton() {
    useOnChangeWatcher(selectedObjects.current);
    const count = selectedObjects.current.value.size;
    return (
      <button className="button white" onClick={assignSelectedConsumers} disabled={count === 0}>
        {count}{" "}
        {itx("cabinetData.assignToT1", "Mit T1 verbinden", {
          T1: device?.name || itx("cabinetData.thisDevice", "diesem Gerät"),
        })}
      </button>
    );
  }

  return (
    <UniversalObjectTable
      title={itx("cabinetData.rightSideList", "Verbindbare Verbraucher")}
      objects={consumers.current}
      selectedObjects={selectedObjects.current}
      width="100%"
      objectColumnConfig={
        new ColumnConfig({
          title: device?.isButton
            ? itx("cabinetData.buttonConnection", "Verbraucher/Scene/Multiplexer")
            : itx("cabinetData.consumer", "Verbraucher"),
          showName: "afterButtons", // no itx
        })
      }
      parentColumnConfig={new ColumnConfig({ title: itx("cabinetData.room", "Raum") })}
      // additionalColumns={[
      //   {
      //     columnConfig: new ColumnConfig({ title: itx("cabinetData.connected", "verbunden") }),
      //     position: "right",
      //     filterFunc: (obj, expr) => {
      //       const lower = expr.toLocaleLowerCase();
      //       const connectable = obj as ConnectableObject;
      //       const connectedChannel = connectable.getConnectedChannel();
      //       const isConnectedToDevice = !!connectedChannel && connectedChannel.parent?.id === device?.id;
      //       const label = isConnectedToDevice ? itx("cabinetData.yes", "ja") : itx("cabinetData.no", "nein");
      //       return label.toLocaleLowerCase().includes(lower);
      //     },
      //     element: (obj) => {
      //       const connectable = obj as ConnectableObject;
      //       const connectedChannel = connectable.getConnectedChannel();
      //       const isConnectedToDevice = !!connectedChannel && connectedChannel.parent?.id === device?.id;
      //       return isConnectedToDevice ? (
      //         <span style={{ paddingLeft: 8, color: "var(--color-success, green)" }}>
      //           {itx("cabinetData.yes", "ja")}
      //         </span>
      //       ) : (
      //         <span style={{ paddingLeft: 8, color: "var(--text-disabled, #aaa)" }}>
      //           {itx("cabinetData.no", "nein")}
      //         </span>
      //       );
      //     },
      //   },
      // ]}
      footer={[<AssignButton key="assign" />]}
    />
  );
}

export default function CabinetPropertiesView(props: { cabinet?: Cabinet; asTab?: boolean; device?: Device }) {
  const { cabinet, asTab, device: fixedDevice } = props;
  useOnChangeWatcher(cabinet);

  const [selectedActorId, setSelectedActorId] = useState<string>("");

  function CabinetData(props: {}) {
    const actors = cabinet?.actors.sortBy("tag").filter((actor) => !channelTypeInfo(actor.channelType).isSupply) ?? [];

    return (
      <div
        style={{
          padding: 12,
          backgroundColor: "var(--panel-background)",
          borderRadius: "var(--panel-border-radius)",
        }}
      >
        <label>{itx("cabinetData.selectActor", "Aktor auswählen:")}</label>
        <select
          className="select"
          value={selectedActorId}
          onChange={(e) => setSelectedActorId(e.target.value)}
          style={{ width: "200px", marginLeft: "10px", height: "35px", border: "solid 1px lightgrey" }}
        >
          {actors.map((actor) => (
            <option key={actor.id} value={actor.id}>
              {actor.name || actor.tag || actor.id}
            </option>
          ))}
        </select>
      </div>
    );
  }

  function getSelectedDevice(): Device | undefined {
    if (fixedDevice) return fixedDevice;
    const actors = cabinet?.actors.sortBy("tag").filter((actor) => !channelTypeInfo(actor.channelType).isSupply) ?? [];
    const actor = actors.find((a) => a.id === selectedActorId) ?? actors[0];
    return actor;
  }

  useEffect(() => {
    if (fixedDevice) return;
    const actor = getSelectedDevice();
    if (actor && selectedActorId !== actor.id) {
      setSelectedActorId(actor.id);
    }
  }, [cabinet]);

  const panelId = `CabinetConsumersLeft_${fixedDevice?.id ?? cabinet?.id ?? "default"}`;

  const innerContent = (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 12, height: "100%", width: "100%", overflow: "hidden" }}
    >
      {!fixedDevice && <CabinetData />}

      <div style={{ display: "flex", flexDirection: "row", height: "100%", minHeight: 0, overflow: "hidden" }}>
        <div id={panelId} style={{ width: "50%", minWidth: 0, overflow: "hidden" }}>
          <LeftSideList device={getSelectedDevice()} />
        </div>
        <PanelResizer leftPanelId={panelId} />
        <div style={{ flex: 1, minWidth: 0, overflow: "hidden", backgroundColor: "var(--panel-background)" }}>
          <RightSideList device={getSelectedDevice()} />
        </div>
      </div>
    </div>
  );

  if (asTab) {
    return innerContent;
  }

  return (
    <div
      id={`pane_${cabinet?.id}`}
      className="cabinetPropertiesView"
      style={{
        position: "absolute",
        width: "calc(100% - 10px)",
        height: "calc(100% - 15px)",
        margin: "5px",
        overflow: "hidden",
        borderRadius: "var(--panel-border-radius)",
        boxShadow: "var(--panel-shadow)",
        display: "flex",
        flexDirection: "column",
        textAlign: "left",
        top: "0",
      }}
    >
      {innerContent}
    </div>
  );
}
